import type { CreateExportRequestV1 } from "@strust/contracts";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import { useApiClient } from "../../../shared/api/api-provider.tsx";
import { createExportEndpoint, listExportsEndpoint } from "./endpoints.ts";

/** The request body as screens build it (defaults such as `sort` are filled in by the schema). */
export type ExportRequest = z.input<typeof CreateExportRequestV1>;

export const EXPORTS_QUERY_KEY = ["exports"] as const;

/** The caller's exports, newest first, with a "Load more" cursor. */
export function useExportsQuery(enabled: boolean) {
  const client = useApiClient();
  return useInfiniteQuery({
    queryKey: EXPORTS_QUERY_KEY,
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ signal, pageParam }) =>
      client.call(listExportsEndpoint, {
        query: pageParam ? { cursor: pageParam } : {},
        signal,
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** Requests an export of what is on screen: a CSV at once, or a background export (202). */
export function useRequestExport() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ExportRequest) => client.callCsv(createExportEndpoint, { body }),
    onSuccess: (outcome) => {
      if (outcome.kind === "json") {
        void queryClient.invalidateQueries({ queryKey: EXPORTS_QUERY_KEY });
      }
    },
  });
}
